import React from 'react';
import { graphql, PageProps } from 'gatsby';
import Layout, { SeoMetadata } from './../components/layout/layout';
import ModularContentArea from '../components/modular-content-area/modular-content-area';
import { DatoCmsPage, PageComponents } from '../schema/datocms';
import { ComponentTypeNames } from '../schema';
import { getTextAlignmentClass } from '../utilities';
import ChatComponent from '../components/block-components/chat-component/chat-component';

const PageTemplate = ({ data, location }: PageProps<PageTemplateQueryResult>): React.ReactElement => {

    const { slug, pageTitle, titleAlignment, titleSuffix, seoMetadata, pageContent, pageSidebarContent, sidebarColumnCount, headerType, hideFooter, showGetQuoteInHeader, hideGlobalBanner, bannerAlert, schemasJsonString, enableChat, canonicalUrl } = data.page;

    const seo: SeoMetadata = {
        pageTitle,
        titleSuffix,
        shareTitle: seoMetadata?.title,
        description: seoMetadata?.description,
        twitterCardSize: seoMetadata?.twitterCard,
        imageUrl: seoMetadata?.image?.fixed.src,
        schemasJsonString
    };

    const hasSidebar: boolean = Boolean(pageSidebarContent && pageSidebarContent.length > 0 && sidebarColumnCount);
    const pageClasses: string = hasSidebar ? 'container mx-auto grid grid-cols-1 lg:grid-cols-12 px-4 md:px-0' : 'px-4 md:px-0';
    const pageContentClone: PageComponents[] = JSON.parse(JSON.stringify(pageContent));
    const hasHero: boolean = pageContentClone && pageContentClone.some(p => p.__typename === ComponentTypeNames.HERO_COMPONENT);
    const topNavBar: boolean = pageContentClone && pageContentClone.length > 0 && pageContentClone[0].__typename === ComponentTypeNames.NAVIGATION_BAR;
    let topContent: JSX.Element | undefined = undefined;
    if (!hasHero || topNavBar) {
        const navBar: PageComponents | undefined = topNavBar ? pageContentClone.shift() : undefined;

        topContent = (
            <div>
                { navBar && <ModularContentArea currentPageSlug={slug} components={[navBar]} />}
                { !hasHero && <div className="container mx-auto px-3 lg:px-6"><div className="grid grid-cols-1 lg:grid-cols-12">
                    <div className="lg:col-start-2 col-span-12">
                        <h1 className={`pt-16 text-primary text-center lg:${getTextAlignmentClass(titleAlignment)}`}>{pageTitle}</h1>
                    </div>
                </div></div>}
            </div>
        );
    }

    // subtract 1 column so that there's a gap between the main content and sidebar content, and another to start in column 2
    const mainContentClasses: string = hasSidebar && sidebarColumnCount ? `lg:col-start-2 lg:col-span-${12 - sidebarColumnCount - 2}` : '';

    const pageBannerAlert = bannerAlert?.length === 1 ? bannerAlert[0] : undefined;

    return (
        <Layout seo={seo} pathname={location.pathname} options={{ headerType, hideFooter, pageBannerAlert, showGetQuoteInHeader, hideGlobalBanner, canonicalUrl }}>
            <div className={pageClasses}>
                { topContent }
                <div className={mainContentClasses}>
                    <ModularContentArea currentPageSlug={slug} components={pageContentClone} />
                </div>
                {
                    hasSidebar && <div className={`lg:col-span-${sidebarColumnCount} lg:col-end-13`}><ModularContentArea components={pageSidebarContent} /></div>
                }
            </div>

            { enableChat && <ChatComponent /> }
        </Layout>
    );
};

export default PageTemplate;

// a hero component wouldn't appear in a sidebar so that is intentionally left out of the query, consider
// that for other components going forward
export const PageTemplateQuery = graphql`
    query PageTemplateQuery($pageId: String) {
        page: datoCmsPage(id: {eq: $pageId}) {
            slug
            pageTitle
            titleAlignment
            titleSuffix
            seoMetadata {
                title
                description
                twitterCard
                image {
                    fixed(width: 1200, imgixParams: { fm: "png" }) {
                        src
                    }
                }
            }
            pageContent {
                __typename
                ...ListComponentFragment
                ...HeroComponentFragment
                ...InformationSectionFragment
                ...MadLibsSectionFragment
                ...BrandLogoSectionComponentFragment
                ...EoFormSectionComponentFragment
                ...ProductComparisonTableFragment
                ...CoverageDetailsGridFragment
                ...FaqListFragment
                ...NavigationBarFragment
                ...AdvisorLeadFormSectionComponentFragment
            }
            pageSidebarContent {
                __typename
                ...ListComponentFragment
                ...InformationSectionFragment
            }
            sidebarColumnCount
            headerType
            hideFooter
            hideGlobalBanner
            showGetQuoteInHeader
            enableChat
            bannerAlert {
                ...BannerAlertFragment
            }
            schemasJsonString
            canonicalUrl {
                __typename
                ... on DatoCmsBlogConfiguration {
                  slug
                }
                ... on DatoCmsBlogPost {
                  slug
                }
                ... on DatoCmsPage {
                  slug
                }
              }
        }
    }
`;

export interface PageTemplateQueryResult {
    page: DatoCmsPage;
}
